"use client"

/* eslint-disable */
import { useState, useEffect, useMemo } from "react"
import { Table, TableHead, TableRow, TableBody, TableCell, TablePagination, Box, Chip } from "@mui/material"
import { usePremiumTokens } from '../common/theme/usePremiumTokens'
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ReceiptIcon from "@mui/icons-material/Receipt"
import CategoryIcon from "@mui/icons-material/Category"
import DevicesIcon from "@mui/icons-material/Devices"
import { useSelector, useDispatch, shallowEqual } from "react-redux"
import { formatTime } from "../common/util/formatter"
import ReportFilter from "./components/ReportFilter"
import { useTranslation } from "../common/components/LocalizationProvider"
import PageLayout from "../common/components/PageLayout"
import ReportsMenu from "./components/ReportsMenu"
import usePersistedState from "../common/util/usePersistedState"
import ColumnSelect from "./components/ColumnSelect"
import { useCatch } from "../reactHelper"
import useReportStyles from "./common/useReportStyles"
import TableShimmer from "../common/components/TableShimmer"
import { utils, writeFileXLSX } from "xlsx"
import { nativePostMessage } from "../common/components/NativeInterface"
import { sessionActions } from "../store"
import { PrintLayout } from "./common/print"
import fetchOrThrow from "../common/util/fetchOrThrow"

// Colunas disponíveis com suas chaves de tradução
const columnsArray = [
  ["eventTime", "costDate"],
  ["deviceId", "sharedDevice"],
  ["name", "sharedName"],
  ["price", "sharedPrice"],
  ["type", "costCategory"],
  ["description", "sharedDescription"],
  ["category", "sharedCategory"],
  ["status", "sharedStatus"],
  ["paymentMethod", "paymentMethod"],
  ["location", "sharedLocation"],
  ["attributes", "sharedAttributes"],
]

// Cria o Map
const columnsMap = new Map(columnsArray)

// A stable empty object used as a fallback
const EMPTY_OBJECT = Object.freeze({})

const CostsReportPage = () => {
  const classes = useReportStyles()
  const t = useTranslation()
  const dispatch = useDispatch()
  const tokens = usePremiumTokens()

  // Use stable empty object fallback and shallow equal to prevent rerenders
  const devices = useSelector((state) => state.devices.items ?? EMPTY_OBJECT, shallowEqual)
  const printing = useSelector((state) => state.session.printing ?? false)

  const [columns, setColumns] = usePersistedState("costsColumns", ["deviceId", "eventTime", "name", "type", "price"])
  const [items, setItems] = useState([])
  const [hasResults, setHasResults] = useState(false)
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [availableColumns, setAvailableColumns] = useState([])
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([])
  const [selectedGroupIds, setSelectedGroupIds] = useState([])
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Stats calculados
  const stats = useMemo(() => {
    if (items.length === 0) return null

    const totalCost = items.reduce((acc, item) => acc + (parseFloat(item.price) || 0), 0)
    const uniqueDevices = new Set(items.filter(i => i.deviceId).map(i => i.deviceId)).size
    const categories = {}
    items.forEach(item => {
      const cat = item.type || item.category || 'other'
      categories[cat] = (categories[cat] || 0) + 1
    })
    const uniqueCategories = Object.keys(categories).length

    return {
      totalRecords: items.length,
      totalCost: totalCost.toFixed(2),
      uniqueDevices,
      uniqueCategories,
    }
  }, [items])

  // Adicionar evento de afterprint para limpar o estado de impressão
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t("reportCosts")}.pdf`)),
      )
    }
  }, [printing, t])

  // Função segura para tradução que retorna o texto original se a tradução for vazia
  const safeTranslate = (key) => {
    const translated = t(key)
    // Se a tradução for vazia, indefinida, ou igual à chave (não traduzida), retorna a chave original
    return !translated || translated === key || translated.trim() === "" ? key : translated
  }

  // Efeito para traduzir as colunas iniciais
  useEffect(() => {
    // Traduzir as colunas disponíveis usando a função de tradução segura
    const translatedColumns = columnsArray.map(([key, translationKey]) => {
      const translated = safeTranslate(translationKey)
      return [key, translated !== translationKey ? translated : key]
    })
    setAvailableColumns(translatedColumns)
  }, [t])

  // Função para buscar dados e extrair colunas disponíveis
  const fetchData = async ({ deviceId, deviceIds = [], groupIds = [], from, to, mail, headers } = {}) => {
    const resolvedDeviceIds = deviceIds.length > 0 ? deviceIds : deviceId ? [deviceId] : []
    setSelectedDeviceIds(resolvedDeviceIds)
    setSelectedGroupIds(groupIds)
    setFromDate(from || "")
    setToDate(to || "")
    setItems([])
    setHasResults(false)
    setLoading(true)
    try {
      const query = new URLSearchParams()
      if (from) query.append("from", from)
      if (to) query.append("to", to)
      if (mail) query.append("mail", mail)
      resolvedDeviceIds.forEach((id) => query.append("deviceId", id))
      groupIds.forEach((id) => query.append("groupId", id))

      const response = await fetchOrThrow(`/api/reports/costs?${query.toString()}`, {
        headers,
      })

      const contentType = response.headers.get("content-type") || ""
      if (contentType.includes("application/json")) {
        const data = await response.json()
        setAllItems(data)
        setItems(data)
        setHasResults(data.length > 0)

        // preserved custom logic: mantemos a extração dinâmica de colunas com atributos aninhados.
        if (data.length > 0) {
          const keySet = new Set()
          data.forEach((item) => {
            Object.keys(item).forEach((key) => {
              if (item[key] !== null && item[key] !== undefined && item[key] !== "") {
                keySet.add(key)
              }
            })

            if (item.attributes) {
              Object.keys(item.attributes).forEach((key) => {
                if (
                  item.attributes[key] !== null &&
                  item.attributes[key] !== undefined &&
                  item.attributes[key] !== ""
                ) {
                  keySet.add(key)
                }
              })
            }
          })

          ;["attributes", "id", "__typename"].forEach((key) => keySet.delete(key))

          const newColumns = [...keySet]
            .map((key) => {
              const translationKey = columnsMap.get(key)
              if (translationKey) {
                const translated = safeTranslate(translationKey)
                return [key, translated !== translationKey ? translated : key]
              }
              return [key, key]
            })
            .sort((a, b) => a[1].localeCompare(b[1]))

          if (data.some((item) => item.attributes && Object.keys(item.attributes).length > 0)) {
            const attributesTranslation = safeTranslate("sharedAttributes")
            newColumns.push(["attributes", attributesTranslation !== "sharedAttributes" ? attributesTranslation : "attributes"])
          }

          setAvailableColumns(newColumns)
        }
        return data
      }

      window.location.assign(window.URL.createObjectURL(await response.blob()))
      setHasResults(true)
    } catch (error) {
      console.error("Error fetching costs:", error)
      setItems([])
      setHasResults(false)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const handleShow = useCatch(async ({ deviceIds = [], groupIds = [], from, to }) => {
    if (!deviceIds.length && !groupIds.length) {
      setItems([])
      setHasResults(false)
      return
    }
    await fetchData({ deviceIds, groupIds, from, to })
    setPage(0)
  })

  const handleExportReport = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const resolvedDeviceIds = deviceIds.length > 0 ? deviceIds : selectedDeviceIds
    const resolvedGroupIds = groupIds.length > 0 ? groupIds : selectedGroupIds
    const effectiveFrom = from ?? fromDate
    const effectiveTo = to ?? toDate
    if (!resolvedDeviceIds.length && !resolvedGroupIds.length) {
      return
    }
    if (!items.length) {
      await fetchData({ deviceIds: resolvedDeviceIds, groupIds: resolvedGroupIds, from: effectiveFrom, to: effectiveTo })
    }
    const table = document.querySelector("table")
    if (!table) {
      return
    }
    requestAnimationFrame(() =>
      writeFileXLSX(utils.table_to_book(table), `${t("reportCosts")}.xlsx`),
    )
  })

  const handlePrintReport = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const resolvedDeviceIds = deviceIds.length > 0 ? deviceIds : selectedDeviceIds
    const resolvedGroupIds = groupIds.length > 0 ? groupIds : selectedGroupIds
    const effectiveFrom = from ?? fromDate
    const effectiveTo = to ?? toDate
    if (!resolvedDeviceIds.length && !resolvedGroupIds.length) {
      return
    }
    if (!items.length) {
      await fetchData({ deviceIds: resolvedDeviceIds, groupIds: resolvedGroupIds, from: effectiveFrom, to: effectiveTo })
    } else {
      setSelectedDeviceIds(resolvedDeviceIds)
      setSelectedGroupIds(resolvedGroupIds)
      setFromDate(effectiveFrom || "")
      setToDate(effectiveTo || "")
    }
    dispatch(sessionActions.setPrinting(true))
  })

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Formatar atributos para exibição
  const formatarAtributos = (atributos) => {
    if (!atributos || Object.keys(atributos).length === 0) {
      return "-"
    }
    return Object.entries(atributos)
      .map(([chave, valor]) => `${chave}: ${valor}`)
      .join(", ")
  }

  const formatValue = (item, key) => {
    switch (key) {
      case "deviceId":
        return devices[item[key]]?.name || item[key]
      case "eventTime":
        return item[key] ? formatTime(item[key], "DD/MM/YYYY") : null
      case "attributes":
        return formatarAtributos(item[key])
      default:
        // Verificar se é um atributo dentro do objeto attributes
        if (item.attributes && item.attributes[key] !== undefined) {
          return item.attributes[key]
        }
        return item[key]
    }
  }

  // Função para obter o nome de exibição da coluna
  const getColumnDisplayName = (key) => {
    const translationKey = columnsMap.get(key)
    if (translationKey) {
      const translated = safeTranslate(translationKey)
      return translated !== translationKey ? translated : key
    }
    return key
  }

  // Layout para modo de impressão
  if (printing) {
    const deviceNames = selectedDeviceIds.map(id => devices[id]?.name || id).filter(Boolean);

    return (
      <PrintLayout
        title={t("reportCosts")}
        deviceName={deviceNames.join(", ")}
        from={fromDate}
        to={toDate}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              {columns.map((key) => (
                <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={`${item.id || item.deviceId}_${item.eventTime || ''}`}>
                {columns.map((key) => (
                  <TableCell key={key}>{formatValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    );
  }

  // O conteúdo que será passado como children para o PageLayout
  const pageContent = (
    <div className={classes.container}>
      <div className={classes.containerMain}>
        <div className={classes.header}>
          {/* standardized export/print/agenda menu */}
          <ReportFilter
            onShow={handleShow}
            onExport={handleExportReport}
            onPrint={handlePrintReport}
            loading={loading}
            hasResults={hasResults}
            deviceType="multiple"
          >
            <Box className={classes.filterItem} sx={{ minWidth: 200 }}>
              <ColumnSelect
                columns={columns}
                setColumns={setColumns}
                columnsArray={availableColumns}
                rawValues={true}
                disabled={loading}
              />
            </Box>
          </ReportFilter>
        </div>

        {/* Stats Cards */}
        {stats && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 2, pb: 1, pt: 1, backgroundColor: tokens.surface.elevated, borderTop: `1px solid ${tokens.border.subtle}` }}>
            <Chip
              icon={<ReceiptIcon sx={{ color: `${tokens.accent.primary} !important` }} />}
              label={`${stats.totalRecords} ${t('sharedTotal')}`}
              sx={{ backgroundColor: tokens.accent.subtle, color: tokens.accent.primary, borderColor: tokens.accent.primary, fontWeight: 500 }}
              variant="outlined"
            />
            <Chip
              icon={<AttachMoneyIcon sx={{ color: `${tokens.semantic.success} !important` }} />}
              label={`${t('reportTotalCost')}: R$ ${stats.totalCost}`}
              sx={{ borderColor: tokens.semantic.success, color: tokens.semantic.success }}
              variant="outlined"
            />
            <Chip
              icon={<DevicesIcon sx={{ color: `${tokens.semantic.info} !important` }} />}
              label={`${stats.uniqueDevices} ${t('sharedDevices')}`}
              sx={{ borderColor: tokens.semantic.info, color: tokens.semantic.info }}
              variant="outlined"
            />
            <Chip
              icon={<CategoryIcon sx={{ color: `${tokens.text.secondary} !important` }} />}
              label={`${stats.uniqueCategories} ${t('sharedCategory')}`}
              sx={{ borderColor: tokens.border.default, color: tokens.text.primary }}
              variant="outlined"
            />
          </Box>
        )}

      <Table>
        <TableHead>
          <TableRow>
            {columns.map((key) => (
              <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? (
            items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
              <TableRow key={`${item.id || item.deviceId}_${new Date().getMilliseconds()}`} hover>
                {columns.map((key) => (
                  <TableCell key={key}>{formatValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableShimmer columns={columns.length} startAction={false} endAction={false} />
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={items.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t("sharedRowsPerPage")}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("sharedOf")} ${count}`}
      />
      </div>
    </div>
  )

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={["reportTitle", "reportCosts"]}>
      {pageContent}
    </PageLayout>
  )
}

export default CostsReportPage
